import classes from "../CorporateFundTransfer/CorporateFundTransfer.module.css";
import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import TextFieldForm from "../../../../components/common/textFieldForm";
import { Box, Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import { postApiData } from "../../../../components/utilities/nodeApiServices";
import { apiList } from "../../../../components/utilities/nodeApiList";
import { useSelector } from "react-redux";
import { errorMessages } from "../../../../components/utilities/formValidation";
import AutocompleteForm from "../../../../components/common/autuCompleteForm";
import SweetAlertPopup from "../../../../components/common/sweetAlertPopup";
import Loader from "../../../../components/common/loader";
import html2canvas from "html2canvas";
import sahyogLogo from "../../../../assets/Banks/Suvarna/images/Logo.svg";
import QRCode from "react-qr-code";
import { compareTextAndReturnObject } from "../../../../components/common/commonArray";
import RadioGroupForm from "../../../../components/common/RedioButtonForm";
import { useCustomButtons } from "../../../../components/common/useCustomButtons";
import { numberCommaFormat } from "../../../../components/utilities/fileUploadHelper";

const defaultFormData = {
    qrType: "Static",
    remark: "",
    amount: "",
    accountNumber: "",
};

const GenerateQRCodeCorporate = () => {
    const { control, handleSubmit, setValue, getValues, watch } = useForm({
        defaultValues: defaultFormData,
        mode: "onChange",
    });

    const { ColorButton2, ColorButton } = useCustomButtons();
    const navigate = useNavigate();

    const [isLoading, setIsloading] = useState(false);
    const [qrResponse, setQrResponse] = useState("");
    const [fetchBalanceData, setFetchBalanceData] = useState(null);
    const [accountBalance, setAccountBalance] = useState({});
    const [qrValue, setQrValue] = useState("");
    const [accountList, setAccountList] = useState([]);
    const qrRef = useRef();

    const { user } = useSelector((state) => state.auth);
    const theme = useSelector((state) => state.theme.theme);

    // Read form values once per render (stable primitives for effect deps)
    const selectedAccount = watch("accountNumber");
    const qrType = watch("qrType");
    const accNoValue = selectedAccount?.value;

    /* ------------------------------------------------------------------ */
    /* API helpers – each one only returns data. No shared loader handling */
    /* here, so one call can never switch the loader off for the next one. */
    /* ------------------------------------------------------------------ */

    const fetchAccountList = async () => {
        try {
            const response = await postApiData(apiList.ACCOUNT_BROWSE_LIST_FOR_QR_GENERATION_CR, {
                username: user?.userId,
                sessionId: user?.sessionId,
            });
            if (response?.data?.status == false) {
                if (response.data.respCode != "IS") {
                    SweetAlertPopup(response.data.message, "Error", "error");
                }
                return [];
            }
            return (
                response?.data?.accountDetails?.map((account) => ({
                    code: account.brCode,
                    value: account.accNo,
                })) || []
            );
        } catch (err) {
            console.error("fetchAccountList error:", err);
            return [];
        }
    };

    const fetchBalance = async (accNo) => {
        try {
            const response = await postApiData(apiList.CORPORATE_FETCH_BALANCE, {
                username: user?.userId,
                accNo,
                sessionId: user?.sessionId,
            });
            if (response?.status == true) return response.data;
            SweetAlertPopup(response?.message, "Error", "error");
            return null;
        } catch (err) {
            console.error("fetchBalance error:", err);
            return null;
        }
    };

    const fetchQRString = async (accNo) => {
        try {
            const response = await postApiData(apiList.QRCODEGWNERATECORPORATE, {
                accountNo: accNo || "",
                username: user?.userId,
                sessionId: user?.sessionId,
            });
            if (response?.status == true) return response?.data?.genrateQr;
            return null;
        } catch (err) {
            console.error("fetchQRString error:", err);
            return null;
        }
    };

    const fetchQRStringDynamic = async (data) => {
        try {
            setIsloading(true);
            const response = await postApiData(apiList.QRCODEGWNERATECORPORATEDYNAMIC, {
                username: user?.userId,
                accountNo: selectedAccount?.value || "",
                custNo: user?.userId,
                sessionId: user?.sessionId,
                remark: data?.remark,
                amount: data?.amount,
                brCode: selectedAccount?.code || "",
            });
            if (response?.status == true) {
                setQrResponse(response?.data?.genrateQr);
                setQrValue(response?.data?.genrateQr?.url);
            }
        } catch (err) {
            console.error("fetchQRStringDynamic error:", err);
        } finally {
            setIsloading(false);
        }
    };

    /* ------------------------------------------------------------------ */
    /* Effects                                                             */
    /* ------------------------------------------------------------------ */

    // STEP 1 – Account list (runs first, once per login session).
    // Selecting the default account here is what triggers STEP 2 and 3 below,
    // so balance can never be called before the account list has loaded.
    useEffect(() => {
        if (!user?.userId || !user?.sessionId) return;

        let cancelled = false;
        const loadAccounts = async () => {
            setIsloading(true);
            try {
                const list = await fetchAccountList();
                if (cancelled) return;
                setAccountList(list);
                if (list.length && !getValues("accountNumber")) {
                    setValue("accountNumber", compareTextAndReturnObject(list, list[0]?.value));
                }
            } finally {
                if (!cancelled) setIsloading(false);
            }
        };

        loadAccounts();
        return () => {
            cancelled = true;
        };
    }, [user?.userId, user?.sessionId]);

    // STEP 2 – balance  ->  STEP 3 – static QR string (only if step 2 succeeded).
    // Runs only after an account exists, i.e. after the account list has loaded.
    useEffect(() => {
        if (!accNoValue) return;

        let cancelled = false; // ignores results of an outdated run

        const run = async () => {
            setIsloading(true);
            setQrValue("");
            try {
                // Fetch balance
                const balance = await fetchBalance(accNoValue);
                if (cancelled) return;
                if (!balance) return;
                setAccountBalance(balance);
                setFetchBalanceData(balance);

                // Fetch QR string – starts only after balance has finished
                if (qrType === "Static") {
                    const qr = await fetchQRString(accNoValue);
                    if (cancelled) return;
                    if (qr) setQrResponse(qr);
                }
            } catch (error) {
                console.error("Error in fetching data:", error);
            } finally {
                if (!cancelled) setIsloading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [accNoValue, qrType]);

    // Reset amount / remark / QR when QR type changes
    useEffect(() => {
        setValue("amount", "");
        setValue("remark", "");
        setQrValue("");
    }, [qrType]);

    /* ------------------------------------------------------------------ */
    /* Handlers                                                            */
    /* ------------------------------------------------------------------ */

    const shareQR = async () => {
        if (!navigator.share) {
            alert("Web Share API is not supported in your browser.");
            return;
        }
        try {
            const canvas = await html2canvas(qrRef.current);
            canvas.toBlob(async (blob) => {
                if (!blob) return;
                const file = new File([blob], "qrcode.png", { type: "image/png" });
                if (navigator.canShare && navigator.canShare({ files: [file] })) {
                    await navigator.share({
                        title: "QR Code",
                        text: "Here is the QR code for payment",
                        files: [file],
                    });
                } else {
                    alert("Sharing images is not supported on this browser.");
                }
            }, "image/png");
        } catch (error) {
            console.error("Error sharing the QR Code image", error);
        }
    };

    const downloadQRCode = () => {
        html2canvas(qrRef.current).then((canvas) => {
            const pngUrl = canvas.toDataURL("image/png");
            const downloadLink = document.createElement("a");
            downloadLink.href = pngUrl;
            downloadLink.download = `${fetchBalanceData?.accName}.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        });
    };

    const handleGenerateQRCodeImage = () => {
        setQrValue(qrResponse?.url);
    };

    const handleResetData = () => {
        setValue("amount", "");
        setValue("remark", "");
        setQrValue("");
    };

    return (
        <>
            <Loader loading={isLoading} />
            <div className={`${classes.redrow} ${theme === "dark" ? classes.DarkTheme : classes.LightTheme}`}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <div className={classes.SubHeading}>Generate QR Code</div>
                    </div>
                </div>
            </div>
            <div className={classes.sbox}>
                <div className={classes.cardsBox}>
                    <div className={classes.accountstatement}>
                        <Box
                            className={classes.mainContainer}
                            component="form"
                            onSubmit={handleSubmit(
                                qrType == "Static" ? handleGenerateQRCodeImage : fetchQRStringDynamic
                            )}
                        >
                            <div className={classes.SboxQR}>
                                <Grid container columnSpacing={2} rowSpacing={2}>
                                    <Grid item xs={12} sm={12} md={8} style={{ padding: "0 50px" }}>
                                        <div>
                                            <div className={classes.formbox}>
                                                <Grid item xs={12} sm={12} md={12} style={{ padding: "15px 0" }}>
                                                    <div className={classes.frowdata11}>
                                                        <div className={classes.frowtext}>
                                                            QR Code Type<sup className={classes.required}>*</sup>
                                                        </div>
                                                        <RadioGroupForm
                                                            controlerProps={{ control: control, name: "qrType" }}
                                                            data={[
                                                                { label: "Static", value: "Static" },
                                                                { label: "Dynamic", value: "Dynamic" },
                                                            ]}
                                                            rules={{ required: "QR Code Type" }}
                                                            errorMessage={"QR Code Type"}
                                                            required={true}
                                                        />
                                                    </div>
                                                </Grid>
                                                <Grid container columnSpacing={2} rowSpacing={2}>
                                                    <Grid item xs={12} sm={12} md={6}>
                                                        <div className={classes.frowdata11}>
                                                            <div className={classes.frowtext}>
                                                                From Account Number<sup className={classes.required}>*</sup>
                                                            </div>
                                                            <AutocompleteForm
                                                                controlerProps={{ control: control, name: "accountNumber" }}
                                                                TextFieldProps={{
                                                                    placeholder: "Select Account Number",
                                                                    onKeyDown: (event) => {
                                                                        const regex = /^[a-zA-Z0-9]*$/;
                                                                        const isBackspace = event.keyCode === 8;
                                                                        const isValidInput = regex.test(event.key);
                                                                        const maxLength = 32;
                                                                        const isMaxLengthReached = event.target.value.length >= maxLength;
                                                                        if ((!isValidInput && !isBackspace) || (isMaxLengthReached && !isBackspace)) {
                                                                            event.preventDefault();
                                                                        }
                                                                    },
                                                                }}
                                                                rules={{
                                                                    required: "Account Number " + errorMessages.error_autocomplete_message,
                                                                }}
                                                                data={accountList}
                                                                required={true}
                                                            />
                                                            <div className={classes.availablebalance}>
                                                                Available Balance:{" "}
                                                                <span className={classes.balace}>
                                                                    {numberCommaFormat(accountBalance?.accBal)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </Grid>

                                                    {qrType == "Static" && (
                                                        <Grid item xs={12} sm={12} md={6} style={{ marginTop: "20px" }}>
                                                            <div style={{ display: "flex", gap: "10px" }}>
                                                                <ColorButton2 variant="contained" type="submit" width="10rem" disabled={isLoading || !qrResponse}>
                                                                    Generate QR
                                                                </ColorButton2>
                                                                <ColorButton variant="contained" type="button" width="10rem" onClick={handleResetData}>
                                                                    Reset
                                                                </ColorButton>
                                                            </div>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </div>

                                            <Grid container columnSpacing={2} rowSpacing={2}>
                                                <Grid item xs={12} sm={12} md={12}>
                                                    {fetchBalanceData != null && (
                                                        <div className={`${classes.qrDataContainer} ${theme === "dark" ? classes.DarkTheme : classes.LightTheme}`}>
                                                            <div className={classes.qrboxData}>
                                                                <div style={{ minWidth: "80px" }}>Name </div>
                                                                <div className={classes.QrCodeNamehead}>
                                                                    :<span className={classes.QrCodeNamehead}>{fetchBalanceData?.accName}</span>
                                                                </div>
                                                            </div>
                                                            <div className={classes.qrboxData}>
                                                                <div style={{ minWidth: "80px" }}>Balance </div>
                                                                <div className={classes.QrCodeNamehead}>
                                                                    :{" "}
                                                                    <span className={classes.QrCodeNamehead}>
                                                                        {numberCommaFormat(fetchBalanceData?.accBal)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Grid>
                                            </Grid>

                                            {qrType == "Dynamic" && (
                                                <div className={classes.formbox}>
                                                    <Grid container columnSpacing={2} rowSpacing={2}>
                                                        <Grid item xs={12} sm={12} md={6}>
                                                            <div className={classes.frowdataaff}>
                                                                <div className={classes.frowtextaff}>
                                                                    Amount<sup className={classes.required}>*</sup>
                                                                </div>
                                                                <div className={classes.frow1aff}>
                                                                    <TextFieldForm
                                                                        controlerProps={{ control: control, name: "amount", rows: 5, maxRows: 10 }}
                                                                        TextFieldProps={{
                                                                            placeholder: "₹ 0.00",
                                                                            fullWidth: true,
                                                                            disabled: qrValue != "",
                                                                            inputProps: { maxLength: 19, style: { textAlign: "right" } },
                                                                            inputLabelProps: { style: { textAlign: "right" } },
                                                                        }}
                                                                        regExp={/^[0-9.]+$/}
                                                                        backgroundColor={qrValue != ""}
                                                                        rules={{
                                                                            required: "Amount" + errorMessages.error_autocomplete_message,
                                                                            pattern: {
                                                                                value: /^(?!0+$)[0-9.]{1,15}$/,
                                                                                message: "Please enter a valid Amount",
                                                                            },
                                                                            validate: (value) => {
                                                                                if (value.length === 0) return "Please enter an amount";
                                                                                if (value.length > 15) return "Amount cannot exceed 15 digits";
                                                                                if (value === "0".repeat(value.length)) return "Amount cannot be all zeros";
                                                                                return true;
                                                                            },
                                                                        }}
                                                                        required={true}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </Grid>

                                                        <Grid item xs={12} sm={12} md={6}>
                                                            <div className={classes.frowdataaff}>
                                                                <div className={classes.frowtextaff}>
                                                                    Remark<sup className={classes.required}>*</sup>
                                                                </div>
                                                                <div className={classes.frow1aff}>
                                                                    <TextFieldForm
                                                                        controlerProps={{ control: control, name: "remark", rows: 5, maxRows: 10 }}
                                                                        TextFieldProps={{
                                                                            placeholder: "Remark",
                                                                            fullWidth: true,
                                                                            disabled: qrValue != "",
                                                                            inputProps: { maxLength: 15 },
                                                                        }}
                                                                        regExp={/^[a-zA-Z0-9., ]+$/}
                                                                        backgroundColor={qrValue != ""}
                                                                        rules={{
                                                                            required: "Remark" + errorMessages.error_autocomplete_message,
                                                                        }}
                                                                        required={true}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </Grid>

                                                        <Grid item xs={12} sm={12} md={6} style={{ marginTop: "20px" }}>
                                                            <div style={{ display: "flex", gap: "10px" }}>
                                                                <ColorButton2 variant="contained" type="submit" disabled={isLoading}>
                                                                    Generate QR
                                                                </ColorButton2>
                                                                <ColorButton variant="contained" type="button" onClick={handleResetData}>
                                                                    Reset
                                                                </ColorButton>
                                                            </div>
                                                        </Grid>
                                                    </Grid>
                                                </div>
                                            )}
                                        </div>
                                    </Grid>

                                    <Grid item xs={12} sm={12} md={4}>
                                        <div
                                            id="print-section"
                                            ref={qrRef}
                                            style={{ padding: "20px 52px", display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center" }}
                                        >
                                            {qrValue && (
                                                <>
                                                    <div style={{ marginTop: "20px", paddingBottom: "10px" }}>
                                                        <img src={sahyogLogo} alt="" width="170" />
                                                    </div>
                                                    <div style={{ marginTop: "10px", display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center" }}>
                                                        <QRCode value={qrValue} style={{ width: "140px", height: "140px" }} size={100} />
                                                        <h5 style={{ paddingTop: "15px", textAlign: "center", color: "#242A42" }}>{fetchBalanceData?.accName}</h5>
                                                        <h5 style={{ textAlign: "center", color: "#172B4D" }}>{accNoValue}</h5>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        {qrValue && (
                                            <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
                                                <ColorButton2 onClick={downloadQRCode}>Download</ColorButton2>
                                                <ColorButton2 onClick={shareQR} style={{ width: "150px" }}>
                                                    Share
                                                </ColorButton2>
                                            </div>
                                        )}
                                    </Grid>
                                </Grid>
                            </div>
                        </Box>
                    </div>
                </div>
            </div>
        </>
    );
};

export default GenerateQRCodeCorporate;